import { observer } from 'mobx-react-lite';
import { OverviewCard } from '../../../../lib/components/atomic/dataDisplay/bonus/overviewCard';
import {
  ArrowForwardIconSvg,
  BankChartIcon,
  CashBanknoteCoinsIcon,
  UsersSendMoneyIcon
} from '../../../../lib/icons';
import { useTranslation } from 'react-i18next';
import Util from '../../../../misc/util';
import { useCreateBonus } from '../hooks/useCreateBonus';
import { getBonusErrorMessage } from '../../../../lib/util/apiErrorMessage';
import { StepCard } from '../../../../lib/components/atomic/dataDisplay/bonus/stepCard';
import { useEffect, useMemo, useState } from 'react';
import { useStepper } from '../../partials/actionStepperPartial';
import { Drawer } from '../../../../lib/components/atomic/dataDisplay/drawer';
import { InfoSection } from '../../../../lib/components/atomic/typography/InfoSection';
import { HintBox } from '../../../../lib/components/atomic/typography/hintBox';
import { ContentDialog } from '../../../../lib/components/compound/dialogs/contentDialog';
import { Switch } from '../../../../lib/components';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback';
import { useStore } from '../../../../hooks/useStore';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { SpecialOccasionTypeEnum } from '@pxtra-client-lib-legacy/gen';

export const Summary = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyStore } = useStore();
  const { newBonus, createBonus, updateBonus, createIsPending, updateIsPending } = useCreateBonus();
  const {
    setActiveStep,
    setEditMode,
    setNextButtonConfig,
    setShowInfoDrawer,
    showInfoDrawer,
    allowNavigation
  } = useStepper();
  const [showDialog, setShowDialog] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);

  const navigateToStep = (step: number) => {
    setActiveStep(step);
    setEditMode(true);
  };

  const isSubmitting = useMemo(() => {
    return createIsPending || updateIsPending;
  }, [createIsPending, updateIsPending]);

  const annualAmount = () => {
    if (newBonus.specialOccasionType === 'ANNIVERSARY')
      return (
        ((newBonus.amount ?? 0) * (newBonus.employees?.length ?? 0)) /
        ((newBonus.anniversaryInterval ?? 1) as number)
      );
    if ((newBonus.interval?.length ?? 0) > 0)
      return (
        (newBonus.interval?.length ?? 0) *
        (newBonus.amount ?? 0) *
        (newBonus.employees?.length ?? 0)
      );
    return (newBonus.amount ?? 0) * (newBonus.employees?.length ?? 0);
  };

  const taxTreatmentName = t(`bonus:${newBonus.taxTreatment}:shortName`);
  const specialOccasionName = t(`bonus:${newBonus.specialOccasionType}`);

  const overviewData = [
    {
      label: t('common:totalAmountPerYear'),
      text:
        Util.formatCurrency(annualAmount()) +
        (newBonus.specialOccasionType === 'ANNIVERSARY' &&
        ((newBonus.anniversaryInterval as number) ?? 1) > 1
          ? ' (⌀ p.a.)'
          : ''),
      icon: BankChartIcon
    },
    {
      label: t('common:perPerson'),
      text: Util.formatCurrency(newBonus.amount ?? 0),
      icon: UsersSendMoneyIcon
    },
    {
      label: t('bonus:taxation'),
      text: [
        taxTreatmentName,
        newBonus.taxTreatment === 'SPECIAL_OCCASION' && `(${specialOccasionName})`
      ]
        .filter(Boolean)
        .join(' '),
      icon: CashBanknoteCoinsIcon
    }
  ];

  const createTags = (...items: (string | undefined | null | false)[]): string[] => {
    return items.filter((item): item is string => Boolean(item));
  };

  const getIntervalTag = () => {
    return (
      newBonus.interval?.length !== 0 &&
      (newBonus.interval?.length === 1
        ? t('common:MONTH_one', { count: 1 })
        : t('common:MONTH_other', { count: newBonus.interval?.length }))
    );
  };

  const getAnniversaryIntervalTag = () => {
    if (newBonus.frequency === 'ONE_TIME') {
      if (newBonus.anniversaryInterval === 1) {
        return t('bonus:firstAnniversary');
      }
      return t('bonus:anniversaryIntervalOneTime', {
        count: newBonus.anniversaryInterval as number
      });
    }

    if (newBonus.anniversaryInterval === 1) {
      return t('bonus:yearly');
    }
    return t('bonus:anniversaryIntervalRecurring', {
      count: newBonus.anniversaryInterval as number
    });
  };

  const stepData = [
    {
      title: t('bonus:titleAndDescription'),
      tags: createTags(
        newBonus.title && t('common:TITLE'),
        newBonus.description && t('common:DESCRIPTION')
      ),
      onClick: () => navigateToStep(1)
    },
    {
      title: t('bonus:payoutType'),
      tags: createTags(
        newBonus.payoutType && t(`bonus:${newBonus.payoutType}:name`),
        newBonus.frequency === 'ONE_TIME' ? t('bonus:oneTime') : t('bonus:recurring'),
        newBonus.payoutMonth && Util.formatDateToMonthYear(newBonus.payoutMonth),
        newBonus.startAccountingPeriod &&
          Util.formatDateToMonthYear(newBonus.startAccountingPeriod),
        newBonus.interval && getIntervalTag(),
        newBonus.endAccountingPeriod && Util.formatDateToMonthYear(newBonus.endAccountingPeriod),
        newBonus.anniversaryInterval && getAnniversaryIntervalTag()
      ),
      onClick: () => navigateToStep(3)
    },
    {
      title: t('common:EMPLOYEES'),
      tags: [newBonus.employees?.length],
      onClick: () => navigateToStep(4)
    },
    {
      title: t('common:message'),
      tags: [
        newBonus.messageTemplate?.name &&
          `${t('bonus:template')}: ${newBonus.messageTemplate?.name}`,
        newBonus.message && t('common:message')
      ],
      onClick: () => navigateToStep(5)
    }
  ];

  const handleConfirm = async () => {
    if (isSubmitting) return;
    try {
      if (newBonus.editingId) {
        await updateBonus(newBonus.editingId ?? '');
        enqueueSnackbar(t('common:savingSuccess'), { variant: 'success' });
      } else {
        await createBonus(companyStore.company?.id ?? '');
        enqueueSnackbar(t('bonus:createSuccess'), { variant: 'success' });
      }
      allowNavigation();
      navigate('/bonus');
    } catch (error) {
      console.log(error);
      enqueueSnackbar(getBonusErrorMessage(error, t, newBonus.editingId), {
        variant: 'error'
      });
    } finally {
      setShowDialog(false);
    }
  };

  const nextButtonConfig = useMemo(
    () => ({
      text: t('common:CONFIRM'),
      endIcon: <ArrowForwardIconSvg />,
      onClick: () => {
        setShowDialog(true);
      }
    }),
    [t, isSubmitting, newBonus.employees?.length]
  );

  useEffect(() => {
    setNextButtonConfig(nextButtonConfig);
  }, [nextButtonConfig, setNextButtonConfig]);

  return (
    <>
      <div className="flex flex-col gap-y-6">
        <div className="flex gap-x-4">
          {overviewData.map((item) => (
            <OverviewCard key={item.label} label={item.label} text={item.text} icon={item.icon} />
          ))}
        </div>
        <div className="flex flex-col gap-y-4 w-full">
          {stepData.map((item) => (
            <StepCard
              key={item.title}
              title={item.title}
              tags={item.tags}
              onClick={() => item.onClick()}
            />
          ))}
        </div>
      </div>

      <Drawer
        label={t('bonus:summaryDrawer:title')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
      >
        <div className="flex flex-col gap-y-6">
          <InfoSection content={t('bonus:summaryDrawer:texts', { returnObjects: true })} />
          <HintBox description={t('bonus:summaryDrawer:hint', { returnObjects: true })} />{' '}
        </div>
      </Drawer>

      <ContentDialog
        open={showDialog}
        title={t('bonus:confirmationDrawer:title')}
        primaryButton={t('common:CONFIRM')}
        tertiaryButton={t('bonus:confirmationDrawer:backButton')}
        primaryButtonDisabled={
          !canSubmit || isSubmitting || (newBonus.employees?.length ?? 0) === 0
        }
        onClose={() => !isSubmitting && setShowDialog(false)}
        onPrimaryButtonClick={handleConfirm}
        onTertiaryButtonClick={() => setShowDialog(false)}
      >
        <>
          <InfoSection content={t('bonus:confirmationDrawer:content', { returnObjects: true })} />
          {(newBonus.employees?.length ?? 0) === 0 && (
            <div className="mt-6">
              <FeedbackAlert
                description={t('export:ERROR_NO_EMPLOYEE_SELECTED')}
                severity="error"
              />
            </div>
          )}
          <div className="mt-6">
            <FeedbackAlert
              description={
                ['BIRTHDAY', 'ANNIVERSARY'].includes(
                  newBonus.specialOccasionType as SpecialOccasionTypeEnum
                )
                  ? t('bonus:confirmationDrawer:hintDay')
                  : t('bonus:confirmationDrawer:hintMonth')
              }
              severity="warning"
            />
          </div>
          <div className="flex gap-x-3 mt-8 items-center">
            <Switch
              label={t('benefitSet:confirmationDialog:legalNote')}
              smallLabel
              onChange={(event, checked) => {
                setCanSubmit(checked);
              }}
              className="ml-0 my-2"
            />
          </div>
        </>
      </ContentDialog>
    </>
  );
});
